import Button from "@/components/atom/Button";
import Input, { Caption } from "@/components/atom/Input";
import AlertGon from "@/components/sub-atom/Alert";
import currency from "@/functions/currency";
import {
  addCarts,
  getFeeAdmin,
  getVouchers,
  revalidateCustom,
} from "@/functions/databaseData";
import { ArrowRightSvg, VoucherOutlineSvg } from "@/public/svg";
import { usePathname, useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useCheckBreakPoint } from "@/functions/usResponsive";
import useDelayedRender from "@/functions/useDelayedRender";
import Link from "next/link";

export default function Rincian({ datas, session, carts, overStock }) {
  const path = usePathname();
  const queryClient = useQueryClient();
  const shouldRendered = useDelayedRender(300);

  const router = useRouter();
  const [loading, setLoading] = useState({});
  const [chooseVoucher, setChooseVoucher] = useState({
    title: carts?.code_voucher,
  });
  const reduceJumlahProduk = datas.reduce(
    (accumulator, [_, item]) => accumulator + item.jumlah,
    0
  );

  useEffect(() => {
    setChooseVoucher({ error: false, success: false, title: carts?.code_voucher });
  }, [carts?.code_voucher, carts?.total_pembayaran]);

  async function handleCheckout() {
    try {
      setLoading((old) => ({ ...old, handleCheckout: true }));
      if (!carts?.fee_admin || Number(carts?.fee_admin) <= 0) {
        const feeAdmin = await getFeeAdmin();
        let res = await addCarts({
          body: {
            id: session?.user?.data?.cart_id,
            fee_admin: feeAdmin?.fee_admin,
            total_pembayaran: carts?.total_pembayaran + feeAdmin?.fee_admin,
            change: "reset",
          },
        });
      }
      revalidateCustom({
        path: "/cart/checkout",
      });
      const section = document.getElementById("btn-checkout");
      if (section) section.click();
    } catch (error) {
      console.log("when checkout : ", error?.message);
    } finally {
      setLoading((old) => ({ ...old, handleCheckout: false }));
    }
  }

  async function handleAddVoucher() {
    try {
      if (!chooseVoucher?.title) throw new Error("Masukkan kode voucher");

      const datasVoucher =
        (await getVouchers({
          token: session?.user?.token,
          cart_id: session?.user?.data?.cart_id,
        })) || [];
      // check vaoucher diclaim or no
      const isListVoucher = datasVoucher?.filter((val) => !val?.claimed);
      if (isListVoucher?.length <= 0) throw new Error("Voucher not found");

      // check list voucher by name
      const val = isListVoucher?.filter((val) => val?.code_voucher == chooseVoucher?.title)?.[0];
      if (!val) throw new Error("Kode voucher tidak ditemukan atau sudah di gunakan");

      // check terdapat product yg sama di dalam voucher tersebut
      if (val.has_matching_product !== true && val.has_matching_product !== "true") throw new Error("Keranjang Anda tidak memenuhi syarat voucher");

      // Normalize
      const mitraId = (session?.user?.data?.mitra_id || "").toUpperCase().trim();
      const voucherCode = (val?.code_voucher || "").toUpperCase().trim();

      // Ambil suffix voucher (contoh: VIP600)
      const voucherSuffix = voucherCode.split("-").pop();

      // Mapping prefix -> keyword voucher (HARUS SAMA DENGAN SUFFIX)
      const voucherRules = [
        {
          prefixes: ["CEO", "KOM", "DIR"],
          keyword: "VIP600",
        },
        {
          prefixes: ["GMN", "MGN", "DEP"],
          keyword: "MG500",
        },
        {
          prefixes: ["SPV", "STF"],
          keyword: "STF400",
        },
        {
          prefixes: ["FER"],
          keyword: "FLE300",
        },
      ];

      // Cari rule berdasarkan suffix EXACT MATCH
      const restrictedRule = voucherRules.find(
        rule => rule.keyword === voucherSuffix
      );
      // Cek apakah voucher ini termasuk voucher level khusus rules includes
      // const restrictedRule = voucherRules.find(rule => voucherCode.includes(rule.keyword) );

      if (restrictedRule) {
        const isAllowed = restrictedRule.prefixes.some(prefix =>
          mitraId.startsWith(prefix)
        );

        if (!isAllowed) throw new Error("Voucher ini hanya tersedia untuk level tertentu");
      }

      // check minimum coucher
      if (carts?.total_pembayaran < val?.minimum_voucher)
        throw new Error(
          `Minimal belanja ${currency(val?.minimum_voucher)}`
        );

      let res = await addCarts({
        body: {
          id: carts?._id,
          code_voucher: val?.code_voucher || chooseVoucher?.title,
          nilai_voucher: val?.nominal_voucher,
          total_pembayaran:
            carts?.total_pembayaran - val?.nominal_voucher <= 0
              ? 0
              : carts?.total_pembayaran - val?.nominal_voucher,
          change: "vouchers",
        },
      });
      queryClient.setQueryData(["carts"], (old) => res);
      setChooseVoucher({
        error: "",
        success: `voucher ${val?.code_voucher.toUpperCase()} berhasil diklaim!`,
        ...val,
      });
    } catch (error) {
      setChooseVoucher((old) => ({
        title: old?.title,
        error: error?.message,
      }));
    }
  }

  const breakpoint = useCheckBreakPoint();

  return (
    shouldRendered && (
      <>
        {/* rincian */}
        {breakpoint == "ui-laptop" ? (
          <div className="w-full gap-4 px-6 py-4 flex items-start justify-between  bg-shadesGON-50 shadow-mediumGON rounded-lg">
            <div className="flex flex-col gap-2 w-full max-w-[40%] items-center">
              <div id="voucher" className="flex items-stretch gap-2 w-full">
                <div>
                  <Input
                    value={chooseVoucher?.title ?? ""}
                    placeholder={
                      loading?.getDataVouchers
                        ? "loading..."
                        : "Masukkan kode Voucher"
                    }
                    disabled={overStock > 0 || loading?.getDataVouchers}
                    onChange={(value) => setChooseVoucher({ title: value })}
                    icon={{
                      left: (props) => <VoucherOutlineSvg {...props} />,
                    }}
                    className="border-greyscaleGON-200"
                  />
                  <p className="px-1 mt-2 text-greyscaleGON-400">kode voucher : {carts?.code_voucher || ''}</p>
                </div>
                <div
                  className={twMerge(
                    chooseVoucher?.title?.length > 0
                      ? "border-primaryGON-100"
                      : "border-primaryGON-50",
                    "border h-full p-3 flex items-center justify-center rounded cursor-pointer"
                  )}
                  onClick={handleAddVoucher}
                >
                  <ArrowRightSvg
                    className={twMerge(
                      chooseVoucher?.title?.length > 0
                        ? "text-primaryGON-400"
                        : "text-primaryGON-50",
                      "h-5 w-5"
                    )}
                  />
                </div>
              </div>
              {chooseVoucher?.error?.length > 0 && (
                <Caption error={true} caption={chooseVoucher?.error} />
              )}
              {chooseVoucher?.success?.length > 0 && (
                <Caption success={true} caption={chooseVoucher?.success} />
              )}
            </div>
            {/* <p>Kode Voucher : ********</p> */}
            <div className="flex justify-between gap-1 w-full max-w-[50%]">
              <div
                id="jumlah-produk"
                className="flex flex-col gap-1 w-full max-w-[292px]"
              >
                <p className="p1-regular text-greyscaleGON-400">
                  Jumlah Produk
                </p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {reduceJumlahProduk} pcs
                </p>
              </div>
              <div
                id="berat-total"
                className="flex flex-col gap-1 w-full max-w-[292px]"
              >
                <p className="p1-regular text-greyscaleGON-400">Berat Total</p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {!carts?.weight ? 0 : carts?.weight} gr
                </p>
              </div>
              <div
                id="total-harga"
                className="flex flex-col gap-1 w-full max-w-[292px]"
              >
                <p className="p1-regular text-greyscaleGON-400">Total Harga</p>
                {carts?.nilai_voucher ? (
                  <>
                    <p className="h6-semiBold text-primaryGON-400">
                      {currency(Math.max(0, (carts?.total_pembayaran || 0) - (carts?.nilai_voucher || 0)))}
                    </p>
                    <p className="p2-semiBold text-greyscaleGON-400 line-through">
                      {currency(carts?.total_pembayaran)}
                    </p>
                  </>
                ) : (
                  <p className="h6-semiBold text-primaryGON-400">
                    {currency(Math.max(0, (carts?.total_pembayaran || carts?.total_pembayaran_normal || carts?.total_tagihan_normal || 0) - (carts?.nilai_voucher || 0)))}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-4">
            <div className="w-full gap-2 px-6 py-4 flex bg-shadesGON-50 shadow-mediumGON rounded-lg">
              <div className="flex flex-col gap-2 w-full items-center">
                <div id="voucher" className="flex gap-2 w-full">
                  <Input
                    value={chooseVoucher?.title ?? ""}
                    placeholder={
                      loading?.getDataVouchers
                        ? "loading..."
                        : "Masukkan kode Voucher"
                    }
                    disabled={overStock > 0 || loading?.getDataVouchers}
                    onChange={(value) => setChooseVoucher({ title: value })}
                    icon={{
                      left: (props) => <VoucherOutlineSvg {...props} />,
                    }}
                    className="border-greyscaleGON-200"
                  />
                  <div
                    className={twMerge(
                      chooseVoucher?.title?.length > 0
                        ? "border-primaryGON-100"
                        : "border-primaryGON-50",
                      "border  p-3 rounded cursor-pointer"
                    )}
                    onClick={handleAddVoucher}
                  >
                    <ArrowRightSvg
                      className={twMerge(
                        chooseVoucher?.title?.length > 0
                          ? "text-primaryGON-400"
                          : "text-primaryGON-50",
                        "h-5 w-5"
                      )}
                    />
                  </div>
                </div>
                {chooseVoucher?.error?.length > 0 && (
                  <Caption error={true} caption={chooseVoucher?.error} />
                )}
                {chooseVoucher?.success?.length > 0 && (
                  <Caption success={true} caption={chooseVoucher?.success} />
                )}
              </div>
            </div>
            <div className="w-full gap-2 px-6 py-4 flex flex-col  bg-shadesGON-50 shadow-mediumGON rounded-lg">
              <div id="jumlah-produk" className="flex gap-1 justify-between">
                <p className="p1-regular text-greyscaleGON-400">
                  Jumlah Produk
                </p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {reduceJumlahProduk} pcs
                </p>
              </div>
              <div id="berat-total" className="flex gap-1 justify-between">
                <p className="p1-regular text-greyscaleGON-400">Berat Total</p>
                <p className="h6-semiBold text-greyscaleGON-700">
                  {!carts?.weight ? 0 : carts?.weight} gr
                </p>
              </div>
              <div
                id="total-harga"
                className="flex gap-1 justify-between items-start"
              >
                <p className="p1-regular text-greyscaleGON-400">Total Harga</p>
                {carts?.nilai_voucher ? (
                  <div className="flex flex-col-reverse items-end">
                    <p className="h6-semiBold text-primaryGON-400">
                      {currency(Math.max(0, (carts?.total_pembayaran || 0) - (carts?.nilai_voucher || 0)))}
                    </p>
                    <p className="p2-semiBold text-greyscaleGON-400 line-through">
                      {currency(carts?.total_pembayaran)}
                    </p>
                  </div>
                ) : (
                  <p className="h6-semiBold text-primaryGON-400">
                    {currency(Math.max(0, (carts?.total_pembayaran || carts?.total_pembayaran_normal || carts?.total_tagihan_normal || 0) - (carts?.nilai_voucher || 0)))}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {overStock > 0 && (
          <div className="w-full">
            <AlertGon
              title={`Oops, ada ${overStock} barang yang tidak bisa diproses.`}
              colors="error"
            />
          </div>
        )}

        {/* konfirmasi */}
        <div className="w-full flex flex-col gap-2 items-end pb-10">
          <div className="w-full md:max-w-[368px]">
            <Link
              className="sr-only"
              href={"/cart/checkout"}
              id="btn-checkout"
              shallow
            />
            <Button
              id="checkout"
              loading={loading?.handleCheckout}
              onClick={handleCheckout}
              label={`Checkout (${reduceJumlahProduk})`}
              disable={
                datas?.length <= 0 ||
                (datas?.length == 1 && overStock > 0) ||
                loading?.handleCheckout
              }
            />
          </div>
          <div className="w-full md:max-w-[368px]">
            <Link href={"/"} shallow aria-disabled={loading.handleCheckout}>
              <Button
                id="tambah-produk"
                label="Tambah Produk"
                variants="outline"
                colors="primary"
              />
            </Link>
          </div>
        </div>
      </>
    )
  );
}
